// Day-precision dual-thumb range slider with a viewport that zooms.
//
// Three layers of state:
//   value (dayRange)   — the actual filter sent to applyFilters
//   viewWindow         — the visible domain of the slider track. Picking a
//                         preset changes BOTH value and viewWindow so the
//                         entire chart rescales — no magnifying glass needed.
//                         Drag the thumbs to narrow value WITHIN viewWindow.
//   bin granularity    — histogram bin size auto-adapts: year bins when the
//                         viewWindow spans many years, month bins for ~1y
//                         windows, day bins for ~30d or smaller.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import {
  MIN_YEAR,
  MIN_DAY,
  MAX_DAY,
  dayIndexToShort,
  dayIndexFromDate,
  dayIndexToDate,
  RANGE_PRESETS,
  presetToRange,
  matchPreset,
} from '../lib/url-state';
import type { RangePresetKey } from '../lib/url-state';
import type { OutbreakWithSources } from '../types/supabase';
import { totalCases } from '../lib/outbreaks';

interface Props {
  value: [number, number]; // day indices — the active filter
  onChange: (next: [number, number]) => void;
  outbreaks: OutbreakWithSources[];
}

const TRACK_HEIGHT = 78;
const HANDLE_SIZE = 18;
const PAD_X = 14;
const DAYS_PER_YEAR = 365.25;

// ===========================================================================
// view-window helpers
// ===========================================================================

/** Pick the smallest preset that fully contains the value. Falls back to
 *  the all-time window. */
function initialViewWindow(value: [number, number]): [number, number] {
  // RANGE_PRESETS is roughly largest-to-smallest; reverse so we prefer
  // the tightest fit first.
  const ordered = [...RANGE_PRESETS].reverse();
  for (const p of ordered) {
    const [pf, pt] = presetToRange(p.key);
    if (value[0] >= pf && value[1] <= pt) return [pf, pt];
  }
  return [MIN_DAY, MAX_DAY];
}

type BinSize = 'year' | 'month' | 'week' | 'day';

function chooseBinSize(span: number): BinSize {
  if (span > 365 * 5) return 'year';
  if (span > 365)     return 'month';
  if (span > 60)      return 'week';
  return 'day';
}

interface Bin {
  /** day-index of the bin's start (inclusive) */
  start: number;
  /** day-index of the bin's end (inclusive) */
  end: number;
  /** Centre of the bin in day-index space (for x positioning) */
  mid: number;
  events: number;
  cases: number;
  deaths: number;
}

function buildBins(viewFrom: number, viewTo: number, outbreaks: OutbreakWithSources[]): { bins: Bin[]; size: BinSize; maxCases: number } {
  const span = viewTo - viewFrom;
  const size = chooseBinSize(span);

  const bins: Bin[] = [];
  if (size === 'year') {
    const yStart = dayIndexToDate(viewFrom).year;
    const yEnd = dayIndexToDate(viewTo).year;
    for (let y = yStart; y <= yEnd; y++) {
      const start = dayIndexFromDate(`${y}-01-01`);
      const end = dayIndexFromDate(`${y}-12-31`);
      bins.push({ start, end, mid: Math.floor((start + end) / 2), events: 0, cases: 0, deaths: 0 });
    }
  } else if (size === 'month') {
    const a = dayIndexToDate(viewFrom);
    const b = dayIndexToDate(viewTo);
    let y = a.year, m = a.month;
    while (y < b.year || (y === b.year && m <= b.month)) {
      const monthStart = dayIndexFromDate(`${y}-${String(m + 1).padStart(2, '0')}-01`);
      const lastDay = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
      const monthEnd = dayIndexFromDate(`${y}-${String(m + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`);
      bins.push({
        start: monthStart, end: monthEnd,
        mid: Math.floor((monthStart + monthEnd) / 2),
        events: 0, cases: 0, deaths: 0,
      });
      m += 1;
      if (m > 11) { m = 0; y += 1; }
    }
  } else if (size === 'week') {
    // 7-day bins anchored at viewFrom
    for (let s = viewFrom; s <= viewTo; s += 7) {
      const e = Math.min(viewTo, s + 6);
      bins.push({ start: s, end: e, mid: Math.floor((s + e) / 2), events: 0, cases: 0, deaths: 0 });
    }
  } else {
    // day bins
    for (let d = viewFrom; d <= viewTo; d++) {
      bins.push({ start: d, end: d, mid: d, events: 0, cases: 0, deaths: 0 });
    }
  }

  // Pour outbreaks into bins. Anchor = the later of start_date and
  // cases_last_updated, so a long-running outbreak that received a new
  // case count this week shows up in this week's bin rather than getting
  // hidden behind its months-old start_date. Mirrors the same OR-on-
  // both-dates semantics that applyFilters() uses for map markers.
  for (const o of outbreaks) {
    const start = dayIndexFromDate(o.start_date);
    const updated = o.cases_last_updated
      ? dayIndexFromDate(o.cases_last_updated)
      : start;
    const anchor = Math.max(start, updated);
    if (anchor < viewFrom || anchor > viewTo) continue;
    let bin: Bin | undefined;
    for (let i = 0; i < bins.length; i++) {
      if (anchor >= bins[i].start && anchor <= bins[i].end) { bin = bins[i]; break; }
    }
    if (!bin) continue;
    bin.events += 1;
    bin.cases += totalCases(o);
    bin.deaths += o.cases_deaths;
  }

  let maxCases = 1;
  for (const b of bins) if (b.cases > maxCases) maxCases = b.cases;

  return { bins, size, maxCases };
}

/** Tick marks to render along the slider, given the current viewWindow. */
function buildTicks(viewFrom: number, viewTo: number, size: BinSize): { day: number; label: string }[] {
  const ticks: { day: number; label: string }[] = [];
  if (size === 'year') {
    // every 5 years
    const aYear = dayIndexToDate(viewFrom).year;
    const bYear = dayIndexToDate(viewTo).year;
    const startTickYear = Math.ceil(aYear / 5) * 5;
    for (let y = startTickYear; y <= bYear; y += 5) {
      const d = dayIndexFromDate(`${y}-01-01`);
      ticks.push({ day: d, label: String(y) });
    }
  } else if (size === 'month') {
    // every 2 or 3 months depending on length
    const a = dayIndexToDate(viewFrom);
    const b = dayIndexToDate(viewTo);
    const monthsSpan = (b.year - a.year) * 12 + (b.month - a.month) + 1;
    const step = monthsSpan > 24 ? 6 : monthsSpan > 12 ? 3 : 2;
    let y = a.year, m = a.month;
    while (y < b.year || (y === b.year && m <= b.month)) {
      const d = dayIndexFromDate(`${y}-${String(m + 1).padStart(2, '0')}-01`);
      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      ticks.push({ day: d, label: m === 0 ? `${y}` : months[m] });
      m += step;
      while (m > 11) { m -= 12; y += 1; }
    }
  } else if (size === 'week') {
    // For week-binned histograms (span ~60d to ~365d): step labels by
    // ~month so they don't crowd each other. Anchor at month boundaries.
    const span = viewTo - viewFrom;
    const a = dayIndexToDate(viewFrom);
    const b = dayIndexToDate(viewTo);
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    if (span > 120) {
      // monthly ticks at the 1st of each month
      let y = a.year, m = a.month;
      // step into next month if we're not on the 1st
      if (a.day > 1) { m += 1; if (m > 11) { m = 0; y += 1; } }
      while (y < b.year || (y === b.year && m <= b.month)) {
        const d = dayIndexFromDate(`${y}-${String(m + 1).padStart(2, '0')}-01`);
        if (d >= viewFrom && d <= viewTo) {
          ticks.push({ day: d, label: m === 0 ? `${y}` : months[m] });
        }
        m += 1;
        if (m > 11) { m = 0; y += 1; }
      }
    } else {
      // biweekly ticks for ~2-month windows
      for (let d = viewFrom; d <= viewTo; d += 14) {
        const dd = dayIndexToDate(d);
        ticks.push({ day: d, label: `${months[dd.month]} ${dd.day}` });
      }
    }
  } else {
    // day bins: scale the tick step with the span so the axis stays
    // readable (~7-9 labels max) instead of one every other day.
    const span = viewTo - viewFrom;
    const step = span > 21 ? 5 : span > 7 ? 3 : 1;
    for (let d = viewFrom; d <= viewTo; d += step) {
      const dd = dayIndexToDate(d);
      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      ticks.push({ day: d, label: `${months[dd.month]} ${dd.day}` });
    }
  }
  return ticks;
}

// ===========================================================================
// component
// ===========================================================================

export function TimelineScrubber({ value, onChange, outbreaks }: Props) {
  const [from, to] = value;
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  // Collapse the histogram/scrubber track to reclaim map space. The
  // preset row + toggle always stay visible. Starts collapsed on mobile
  // (Tailwind `sm` = 640px) where vertical space is tight; expanded on
  // desktop. Evaluated once at mount.
  const [open, setOpen] = useState(
    () => typeof window === 'undefined' || window.innerWidth >= 640,
  );
  const [drag, setDrag] = useState<'from' | 'to' | 'range' | null>(null);
  const dragStartRef = useRef<{ mouseX: number; from: number; to: number } | null>(null);

  const [viewWindow, setViewWindow] = useState<[number, number]>(() => initialViewWindow(value));

  // If the URL/parent state moves dayRange OUTSIDE the current viewWindow
  // (e.g., back-button to a different deep link), zoom out to fit.
  useEffect(() => {
    if (from < viewWindow[0] || to > viewWindow[1]) {
      setViewWindow(initialViewWindow(value));
    }
  }, [from, to, viewWindow, value]);

  useEffect(() => {
    if (!trackRef.current) return;
    const ro = new ResizeObserver(entries => {
      for (const e of entries) setWidth(e.contentRect.width);
    });
    ro.observe(trackRef.current);
    return () => ro.disconnect();
  }, []);

  const [viewFrom, viewTo] = viewWindow;
  const span = Math.max(1, viewTo - viewFrom);

  // Histogram + ticks rebuilt whenever viewWindow or outbreaks change.
  const { bins, size: binSize, maxCases } = useMemo(
    () => buildBins(viewFrom, viewTo, outbreaks),
    [viewFrom, viewTo, outbreaks],
  );
  const ticks = useMemo(() => buildTicks(viewFrom, viewTo, binSize), [viewFrom, viewTo, binSize]);

  // ----- coordinate mapping (relative to viewWindow) ----------------------

  const dayToX = useCallback(
    (d: number) => {
      const inner = Math.max(0, width - PAD_X * 2);
      return PAD_X + ((d - viewFrom) / span) * inner;
    },
    [viewFrom, span, width],
  );

  const xToDay = useCallback(
    (x: number) => {
      const inner = Math.max(1, width - PAD_X * 2);
      const t = (x - PAD_X) / inner;
      return Math.round(viewFrom + Math.max(0, Math.min(1, t)) * span);
    },
    [viewFrom, span, width],
  );

  // Bin width in pixels — used for histogram bar widths.
  const binWidthPx = useMemo(() => {
    if (bins.length === 0 || width <= 0) return 0;
    const inner = width - PAD_X * 2;
    return Math.max(1.5, inner / bins.length - 1.5);
  }, [bins.length, width]);

  // ----- drag handling -----------------------------------------------------

  useEffect(() => {
    if (!drag) return;
    const onMove = (e: MouseEvent | TouchEvent) => {
      const trackRect = trackRef.current?.getBoundingClientRect();
      if (!trackRect) return;
      const clientX = (e as TouchEvent).touches?.[0]?.clientX ?? (e as MouseEvent).clientX;
      const x = clientX - trackRect.left;
      const d = xToDay(x);

      if (drag === 'from') onChange([Math.min(d, to), to]);
      else if (drag === 'to') onChange([from, Math.max(d, from)]);
      else if (drag === 'range') {
        const start = dragStartRef.current;
        if (!start) return;
        const deltaDays = xToDay(x) - xToDay(start.mouseX);
        const range = start.to - start.from;
        let nf = start.from + deltaDays;
        let nt = start.to + deltaDays;
        if (nf < viewFrom) { nf = viewFrom; nt = viewFrom + range; }
        if (nt > viewTo) { nt = viewTo; nf = viewTo - range; }
        onChange([nf, nt]);
      }
    };
    const onUp = () => setDrag(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove);
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [drag, from, to, viewFrom, viewTo, onChange, xToDay]);

  const beginDrag = (which: 'from' | 'to' | 'range') => (e: React.MouseEvent | React.TouchEvent) => {
    const trackRect = trackRef.current?.getBoundingClientRect();
    if (!trackRect) return;
    const clientX = (e as React.TouchEvent).touches?.[0]?.clientX ?? (e as React.MouseEvent).clientX;
    dragStartRef.current = { mouseX: clientX - trackRect.left, from, to };
    setDrag(which);
    e.preventDefault();
  };

  const onKey = (which: 'from' | 'to') => (e: React.KeyboardEvent) => {
    let delta = 0;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') delta = -1;
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') delta = 1;
    if (e.key === 'PageDown') delta = -7;
    if (e.key === 'PageUp')   delta = +7;
    if (e.key === 'Home') {
      if (which === 'from') return onChange([viewFrom, to]);
      return onChange([from, from + 1]);
    }
    if (e.key === 'End') {
      if (which === 'from') return onChange([viewTo - 1, to]);
      return onChange([from, viewTo]);
    }
    if (delta === 0) return;
    e.preventDefault();
    if (which === 'from') {
      const nf = Math.max(viewFrom, Math.min(to, from + delta));
      onChange([nf, to]);
    } else {
      const nt = Math.min(viewTo, Math.max(from, to + delta));
      onChange([from, nt]);
    }
  };

  // ----- selection aggregates ----------------------------------------------

  // ----- preset clicks -----------------------------------------------------

  const handlePresetClick = (key: typeof RANGE_PRESETS[number]['key']) => {
    const range = presetToRange(key);
    setViewWindow(range);
    onChange(range); // also reset filter to the full window
  };

  const fromX = dayToX(from);
  const toX = dayToX(to);
  const fromLabel = dayIndexToShort(from);
  const toLabel = dayIndexToShort(to);
  const activePreset = matchPreset([from, to]) ?? matchPreset(viewWindow);

  return (
    <div className="select-none px-4 py-3">
      {/* presets + range readout in one row — stats live in the sidebar
          (single source of truth, no chance of drift) */}
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        {/* Desktop: full chip row. */}
        <div className="hidden items-center gap-1 sm:flex">
          {RANGE_PRESETS.map(p => (
            <PresetButton
              key={p.key}
              label={p.label}
              active={activePreset === p.key}
              onClick={() => handlePresetClick(p.key)}
            />
          ))}
        </div>
        {/* Mobile: collapse the chips into a dropdown to save space. */}
        <div className="relative inline-flex items-center sm:hidden">
          <select
            value={activePreset ?? ''}
            onChange={e => handlePresetClick(e.target.value as RangePresetKey)}
            aria-label="Date range preset"
            className="h-7 cursor-pointer appearance-none rounded-md border border-[var(--color-line)] bg-[var(--color-surface-2)] py-0 pl-2.5 pr-7 text-[11.5px] font-medium text-[var(--color-text-2)] transition-colors hover:border-[var(--color-line-strong)] hover:bg-white hover:text-[var(--color-text)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]/40"
          >
            {activePreset === null && (
              <option value="" disabled>Custom range</option>
            )}
            {/* Shortest → longest (7 days first, All time last). */}
            {[...RANGE_PRESETS].reverse().map(p => (
              <option key={p.key} value={p.key}>{p.label}</option>
            ))}
          </select>
          <ChevronDown
            size={13}
            strokeWidth={2.25}
            className="pointer-events-none absolute right-2 text-[var(--color-text-3)]"
          />
        </div>
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          aria-expanded={open}
          title={open ? 'Hide timeline' : 'Show timeline'}
          className="inline-flex items-center gap-1.5 rounded-md px-1.5 py-1 text-[12px] text-[var(--color-text-2)] transition-colors hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]"
        >
          <span className="text-[12px] font-medium text-[var(--color-text-2)]">
            {open ? 'Hide timeline' : 'Show timeline'}
          </span>
          <ChevronDown
            size={13}
            strokeWidth={2.25}
            className={
              'shrink-0 text-[var(--color-text-3)] transition-transform ' +
              (open ? '' : 'rotate-180')
            }
          />
        </button>
      </div>

      {/* Collapsible track. Kept mounted (never unmounted) so trackRef's
          ResizeObserver stays attached; we animate height/opacity instead. */}
      <div
        className="overflow-hidden transition-[height,opacity,margin] duration-200 ease-out"
        style={{
          height: open ? TRACK_HEIGHT : 0,
          opacity: open ? 1 : 0,
          marginTop: open ? undefined : -8,
        }}
        aria-hidden={!open}
      >
      <div ref={trackRef} className="relative w-full" style={{ height: TRACK_HEIGHT }}>
        {width > 0 && (
          <svg
            width={width}
            height={TRACK_HEIGHT}
            className="absolute inset-0 overflow-hidden"
            aria-hidden
          >
            <defs>
              <linearGradient id="hist-bar" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"  stopColor="var(--color-accent)" stopOpacity="0.9" />
                <stop offset="100%" stopColor="var(--color-accent)" stopOpacity="0.55" />
              </linearGradient>
              <linearGradient id="hist-bar-out" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"  stopColor="#94a3b8" stopOpacity="0.7" />
                <stop offset="100%" stopColor="#94a3b8" stopOpacity="0.4" />
              </linearGradient>
            </defs>

            {/* histogram bars */}
            {bins.map((b, i) => {
              if (b.events === 0) return null;
              const x = dayToX(b.mid);
              const inSelection = b.end >= from && b.start <= to;
              const heightPx = Math.max(2, (b.cases / maxCases) * (TRACK_HEIGHT - 36));
              const tooltipDate =
                binSize === 'year' ? dayIndexToDate(b.mid).year :
                binSize === 'month' ? `${dayIndexToShort(b.mid).split(' ').slice(1).join(' ')}` :
                dayIndexToShort(b.mid);
              return (
                <g key={i}>
                  <rect
                    x={x - binWidthPx / 2}
                    y={TRACK_HEIGHT - 28 - heightPx}
                    width={binWidthPx}
                    height={heightPx}
                    fill={inSelection ? 'url(#hist-bar)' : 'url(#hist-bar-out)'}
                    rx={1.5}
                  >
                    <title>{tooltipDate} · {b.events} events · {b.cases.toLocaleString()} cases · {b.deaths.toLocaleString()} deaths</title>
                  </rect>
                  {b.deaths > 0 && (
                    <line
                      x1={x - binWidthPx / 2}
                      x2={x + binWidthPx / 2}
                      y1={TRACK_HEIGHT - 28 - heightPx - 1.5}
                      y2={TRACK_HEIGHT - 28 - heightPx - 1.5}
                      stroke={inSelection ? 'var(--color-signal)' : '#9ca3af'}
                      strokeWidth={1.5}
                      strokeLinecap="round"
                    />
                  )}
                </g>
              );
            })}

            {/* baseline track */}
            <line
              x1={PAD_X}
              x2={width - PAD_X}
              y1={TRACK_HEIGHT - 26}
              y2={TRACK_HEIGHT - 26}
              stroke="var(--color-line-strong)"
              strokeWidth={1}
            />
            <line
              x1={fromX}
              x2={toX}
              y1={TRACK_HEIGHT - 26}
              y2={TRACK_HEIGHT - 26}
              stroke="var(--color-accent)"
              strokeWidth={2}
              strokeLinecap="round"
            />

            {/* ticks */}
            {ticks.map((t, i) => {
              const x = dayToX(t.day);
              if (x < PAD_X - 4 || x > width - PAD_X + 4) return null;
              const inSel = t.day >= from && t.day <= to;
              return (
                <g key={i}>
                  <line
                    x1={x}
                    x2={x}
                    y1={TRACK_HEIGHT - 26}
                    y2={TRACK_HEIGHT - 22}
                    stroke="var(--color-text-4)"
                    strokeOpacity={0.7}
                    strokeWidth={1}
                  />
                  <text
                    x={x}
                    y={TRACK_HEIGHT - 8}
                    textAnchor="middle"
                    fontFamily="JetBrains Mono, ui-monospace"
                    fontSize={10}
                    fill={inSel ? 'var(--color-text-2)' : 'var(--color-text-4)'}
                  >
                    {t.label}
                  </text>
                </g>
              );
            })}
          </svg>
        )}

        {/* draggable range bar */}
        <div
          onMouseDown={beginDrag('range')}
          onTouchStart={beginDrag('range')}
          className="absolute"
          style={{
            left: fromX,
            width: Math.max(0, toX - fromX),
            top: TRACK_HEIGHT - 32,
            height: 14,
            cursor: drag === 'range' ? 'grabbing' : 'grab',
          }}
          aria-hidden
        />

        <Handle
          x={fromX} y={TRACK_HEIGHT - 26} label={fromLabel}
          dragging={drag === 'from'}
          onMouseDown={beginDrag('from')} onTouchStart={beginDrag('from')}
          onKeyDown={onKey('from')} ariaLabel={`from ${fromLabel}`}
        />
        <Handle
          x={toX} y={TRACK_HEIGHT - 26} label={toLabel}
          dragging={drag === 'to'}
          onMouseDown={beginDrag('to')} onTouchStart={beginDrag('to')}
          onKeyDown={onKey('to')} ariaLabel={`to ${toLabel}`}
        />
      </div>
      </div>
    </div>
  );
}

// ===========================================================================
// helpers
// ===========================================================================

function PresetButton({
  label, active, onClick,
}: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={
        'inline-flex h-7 items-center rounded-md border px-2.5 text-[11.5px] font-medium transition-colors ' +
        (active
          ? 'border-[var(--color-accent)] bg-[var(--color-accent)] text-white shadow-[0_1px_2px_rgba(82,71,229,0.18)]'
          : 'border-[var(--color-line)] bg-[var(--color-surface-2)] text-[var(--color-text-2)] hover:border-[var(--color-line-strong)] hover:bg-white hover:text-[var(--color-text)]')
      }
    >
      {label}
    </button>
  );
}

function Handle({
  x, y, label, dragging, onMouseDown, onTouchStart, onKeyDown, ariaLabel,
}: {
  x: number; y: number; label: string;
  dragging: boolean;
  onMouseDown: (e: React.MouseEvent) => void;
  onTouchStart: (e: React.TouchEvent) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  ariaLabel: string;
}) {
  return (
    <div
      role="slider"
      tabIndex={0}
      aria-label={ariaLabel}
      onMouseDown={onMouseDown}
      onTouchStart={onTouchStart}
      onKeyDown={onKeyDown}
      className="absolute flex flex-col items-center"
      style={{
        left: x - HANDLE_SIZE / 2,
        top: y - HANDLE_SIZE / 2,
        width: HANDLE_SIZE,
        height: HANDLE_SIZE,
        cursor: dragging ? 'grabbing' : 'grab',
        outline: 'none',
      }}
    >
      <div
        className="relative flex items-center justify-center rounded-full bg-white"
        style={{
          width: HANDLE_SIZE,
          height: HANDLE_SIZE,
          border: '1.5px solid var(--color-accent)',
          boxShadow: dragging
            ? '0 0 0 5px var(--color-accent-ring), 0 1px 3px rgba(0,0,0,0.18)'
            : '0 1px 3px rgba(0,0,0,0.12), 0 0 0 1px rgba(82,71,229,0.10)',
          transition: 'box-shadow 140ms ease',
        }}
      >
        <span
          className="block h-1.5 w-1.5 rounded-full"
          style={{ background: 'var(--color-accent)' }}
        />
      </div>
      <div
        className="mono pointer-events-none mt-1 whitespace-nowrap rounded-md bg-[var(--color-text)] px-1.5 py-0.5 text-[10px] font-medium text-white shadow-md"
        style={{ display: dragging ? 'block' : 'none' }}
      >
        {label}
      </div>
    </div>
  );
}

// silence unused — exported for completeness elsewhere
void MIN_YEAR; void DAYS_PER_YEAR;
